"use client";

/**
 * Shared PortableText component factory for next-sanity.
 *
 * This module lives in the UI package because it only needs Typography and class names:
 * it renders standard block content (paragraphs, headings), lists (bullet/number), and
 * link marks (including optional open-in-new-tab) in a schema-agnostic way. Any app can
 * call getPortableTextComponents() with its own CSS class names and get consistent
 * block/list/link rendering.
 *
 * Custom block types (e.g. image, button) are NOT implemented here because they depend on:
 * - App or Sanity schema (block shape, asset refs)
 * - Resolved data (e.g. imageDataMap with URLs/dimensions from Sanity)
 * - Next.js Image or other app-specific components
 * Apps that need them pass options.types when calling getPortableTextComponents().
 */
import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import { cmsTypographyVariantKeys, Typography, type TypographyVariantName, typographyVariants } from "@repo/ui/aops-components/typography";
import { cn } from "@repo/ui/lib/utils";
export type PortableTextClassNames = {
  paragraph?: string;
  heading?: string;
  list?: string;
  listItem?: string;
  listNumber?: string;
  listItemNumber?: string;
};

/** Legacy Portable Text block styles (standard h1–h6 + normal) → PACT variants. */
const legacyPortableTextBlockVariants = {
  normal: "body",
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h4",
  h6: "h4"
} as const satisfies Record<string, TypographyVariantName>;
function isHeadingTypographyVariant(variant: TypographyVariantName): boolean {
  return variant === "h1" || variant === "h2" || variant === "h3" || variant === "h4" || variant === "stat" || variant.startsWith("display");
}
function getBlockClassName(variant: TypographyVariantName, classNames: {
  paragraph: string;
  heading: string;
}): string {
  if (isHeadingTypographyVariant(variant)) {
    return classNames.heading;
  }
  return classNames.paragraph;
}
function createBlockHandler(variant: TypographyVariantName, className: string): ({
  children
}: {
  children?: ReactNode;
}) => React.ReactElement {
  const BlockComponent = t0 => {
    const $ = _c(2);
    const {
      children
    } = t0;
    let t1;
    if ($[0] !== children) {
      t1 = <Typography variant={variant} className={className}>{children}</Typography>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  };
  BlockComponent.displayName = `BlockHandler(${variant})`;
  return BlockComponent;
}
function createPortableTextBlockHandlers(classNames: {
  paragraph: string;
  heading: string;
}): Record<string, ({
  children
}: {
  children?: ReactNode;
}) => React.ReactElement> {
  const handlers: Record<string, ({
    children
  }: {
    children?: ReactNode;
  }) => React.ReactElement> = {};
  for (const [style, variant] of Object.entries(legacyPortableTextBlockVariants)) {
    handlers[style] = createBlockHandler(variant, getBlockClassName(variant, classNames));
  }
  for (const variant of cmsTypographyVariantKeys) {
    handlers[variant] = createBlockHandler(variant, getBlockClassName(variant, classNames));
  }
  return handlers;
}

/** Props passed by next-sanity to custom block type components. We only use value; rest are for compatibility. */
export type PortableTextTypeComponentProps = {
  value: unknown;
  index?: number;
  isInline?: boolean;
  renderNode?: unknown;
};

/** Type for custom block types (e.g. image, button). Callers merge these via options.types. */
export type PortableTextCustomTypes = Record<string, React.ComponentType<PortableTextTypeComponentProps>>;
export type PortableTextLinkMarkValue = {
  href?: string;
  openInNewTab?: boolean;
};
export type GetPortableTextComponentsOptions = {
  classNames: PortableTextClassNames;
  types?: PortableTextCustomTypes;
};
function LinkMark(t0) {
  const $ = _c(8);
  const {
    children,
    value
  } = t0;
  const href = value?.href;
  if (!href) {
    let t1;
    if ($[0] !== children) {
      t1 = <>{children}</>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const openInNewTab = value?.openInNewTab === true;
  let t1;
  if ($[2] !== openInNewTab) {
    t1 = openInNewTab ? {
      target: "_blank",
      rel: "noopener noreferrer"
    } : undefined;
    $[2] = openInNewTab;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== children || $[5] !== href || $[6] !== t1) {
    t2 = <a href={href} {...t1}>{children}</a>;
    $[4] = children;
    $[5] = href;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

/**
 * Builds PortableText components (block, list, listItem, marks) for use with
 * next-sanity's <PortableText components={...} />. Pass class names from your
 * app's CSS module. Optional types (e.g. image, button) are provided by the app
 * and merged in here—the UI package does not define them, since they depend on
 * schema and resolved data.
 */
export function getPortableTextComponents(options: GetPortableTextComponentsOptions): {
  block: Record<string, ({
    children
  }: {
    children?: ReactNode;
  }) => React.ReactElement>;
  list: {
    bullet: ({
      children
    }: {
      children?: ReactNode;
    }) => React.ReactElement;
    number: ({
      children
    }: {
      children?: ReactNode;
    }) => React.ReactElement;
  };
  listItem: {
    bullet: ({
      children
    }: {
      children?: ReactNode;
    }) => React.ReactElement;
    number: ({
      children
    }: {
      children?: ReactNode;
    }) => React.ReactElement;
  };
  marks: {
    link: typeof LinkMark;
  };
  types?: PortableTextCustomTypes;
} {
  const {
    paragraph = "",
    heading = "",
    list = "",
    listItem = "",
    listNumber,
    listItemNumber
  } = options.classNames;
  const listNum = listNumber ?? list;
  const listItemNum = listItemNumber ?? listItem;
  const block = createPortableTextBlockHandlers({
    paragraph,
    heading
  });
  const listComponents = {
    bullet: ({
      children
    }: {
      children?: ReactNode;
    }) => <ul className={list}>{children}</ul>,
    number: ({
      children
    }: {
      children?: ReactNode;
    }) => <ol className={listNum}>{children}</ol>
  };
  const listItemComponents = {
    bullet: ({
      children
    }: {
      children?: ReactNode;
    }) => <li className={cn(typographyVariants({
      variant: "body"
    }), listItem)}>
				{children}
			</li>,
    number: ({
      children
    }: {
      children?: ReactNode;
    }) => <li className={cn(typographyVariants({
      variant: "body"
    }), listItemNum)}>
				{children}
			</li>
  };
  const result: ReturnType<typeof getPortableTextComponents> = {
    block,
    list: listComponents,
    listItem: listItemComponents,
    marks: {
      link: LinkMark
    }
  };
  if (options.types && Object.keys(options.types).length > 0) {
    result.types = options.types;
  }
  return result;
}